import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ApplicationDataService } from '../../core/services/application-data.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-dashboard-delay',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatNativeDateModule,
    MatPaginatorModule,
    ReactiveFormsModule
  ],
  providers: [DatePipe],
  templateUrl: './dashboard-delay.component.html',
  styleUrls: ['./dashboard-delay.component.css']
})
export class DashboardDelayComponent implements OnInit {
  form: FormGroup;

  categoriesData: any[] = [];
  usersDataOriginal: any[] = [];
  usersData: any[] = [];

  agingData: any[] = [];
  agingDataSource = new MatTableDataSource<any>(this.agingData);

  usersList: any[] = [];

  totalCategories = 0;
  totalUsers = 0;

  // Pagination
  pageNo = 1;
  pageSize = 50;
  totalRows = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private fb: FormBuilder,
    private appDataService: ApplicationDataService,
    private datePipe: DatePipe
  ) {
    this.form = this.fb.group({
      dateFrom: [null],
      dateTo: [null],
      days: [4, [Validators.required, Validators.min(1)]],
      assignTo: ['']
    }, { validators: this.dateRangeValidator });
  }

  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const dateFrom = control.get('dateFrom')?.value;
    const dateTo = control.get('dateTo')?.value;

    if (dateFrom && dateTo && dateFrom > dateTo) {
      return { dateRangeInvalid: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadData();
  }

  get f() { return this.form.controls; }

  loadUsers() {
    this.appDataService.getAllInternalUsers().subscribe({
      next: (res: any) => {
        this.usersList = res?.data || [];
        this.usersList.sort((a, b) => a.name.localeCompare(b.name));
      },
      error: () => {
        this.usersList = [];
      }
    });
  }

  clearFilters() {
    this.form.patchValue({
      dateFrom: null,
      dateTo: null,
      days: 4,
      assignTo: ''
    });
    this.pageNo = 1;
    this.loadData();
  }

  applyFilters() {
    this.pageNo = 1;
    this.loadData();
  }

  getPayload(includeAssignTo: boolean = true) {
    let df = this.f['dateFrom'].value;
    let dt = this.f['dateTo'].value;

    const payload: any = {
      days: this.f['days'].value || 4
    };

    payload.dateFrom = df ? this.datePipe.transform(df, 'yyyy-MM-dd') : null;
    payload.dateTo = dt ? this.datePipe.transform(dt, 'yyyy-MM-dd') : null;

    if (includeAssignTo && this.f['assignTo'].value) {
      payload.assignTo = this.f['assignTo'].value;
    } else if (includeAssignTo) {
      payload.assignTo = null;
    }

    return payload;
  }

  loadData() {
    this.loadCategories();
    this.loadUsersWise();
    this.loadAgingDetails();
  }

  loadCategories() {
    this.categoriesData = [];
    this.totalCategories = 0;
    this.appDataService.getDelayCategoryWise(this.getPayload(true)).subscribe({
      next: (res: any) => {
        this.categoriesData = res?.data || [];
        this.totalCategories = this.categoriesData.reduce((acc, cur) => acc + (cur.count || cur.no_of_complaints || 0), 0);
      },
      error: () => {
        this.categoriesData = [];
        this.totalCategories = 0;
      }
    });
  }

  loadUsersWise() {
    this.usersDataOriginal = [];
    this.usersData = [];
    this.totalUsers = 0;
    this.appDataService.getDelayUserWise(this.getPayload(false)).subscribe({
      next: (res: any) => {
        this.usersDataOriginal = res?.data || [];
        this.applyUserWiseFrontendFilter();
      },
      error: () => {
        this.usersDataOriginal = [];
        this.usersData = [];
        this.totalUsers = 0;
      }
    });
  }

  applyUserWiseFrontendFilter() {
    const assignedId = this.f['assignTo'].value;
    if (assignedId) {
      const selectedUser = this.usersList.find(u => u.userId === assignedId);
      if (selectedUser) {
        this.usersData = this.usersDataOriginal.filter(u =>
          u.username === selectedUser.userName ||
          u.grmis_users === selectedUser.userName ||
          u.grmisUsers === selectedUser.userName
        );
      } else {
        this.usersData = [];
      }
    } else {
      this.usersData = [...this.usersDataOriginal];
    }

    this.usersData.sort((a, b) => {
      const aCount = a.count ?? a.no_of_complaints ?? a.overdue ?? 0;
      const bCount = b.count ?? b.no_of_complaints ?? b.overdue ?? 0;
      return bCount - aCount;
    });

    this.totalUsers = this.usersData.reduce((acc, cur) => acc + (cur.count ?? cur.no_of_complaints ?? cur.overdue ?? 0), 0);
  }

  loadAgingDetails() {
    this.agingData = [];
    this.totalRows = 0;
    this.agingDataSource.data = [];
    
    const payload = this.getPayload(true);
     
    this.appDataService.getDelayAgingList(payload).subscribe({
      next: (res: any) => {
        this.agingData = res?.data?.content || res?.data || [];
        this.totalRows = res?.data?.totalElements || this.agingData.length;
        this.agingDataSource.data = this.agingData;
      },
      error: () => {
        this.agingData = [];
        this.totalRows = 0;
        this.agingDataSource.data = [];
      }
    });
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageNo = event.pageIndex + 1;
    this.loadAgingDetails();
  }

  exportExcel() {
    if (!this.categoriesData.length && !this.usersData.length && !this.agingData.length) {
      alert("No data available to export");
      return;
    }

    const wb = XLSX.utils.book_new();

    const catSheetData = this.categoriesData.map(c => ({
      'Complainant Type': c.complainantType || c.complainant_type,
      'Complaint Type': c.complaintType || c.complaint_type,
      'Count': c.count || c.no_of_complaints || 0
    }));
    catSheetData.push({ 'Complainant Type': 'TOTAL', 'Complaint Type': '', 'Count': this.totalCategories });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(catSheetData), "category wise");

    const userSheetData = this.usersData.map(u => ({
      'Name': u.name,
      'Username': u.username || u.grmis_users || u.grmisUsers,
      'Count': u.count || u.no_of_complaints || u.overdue || 0
    }));
    userSheetData.push({ 'Name': 'TOTAL', 'Username': '', 'Count': this.totalUsers });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userSheetData), "user wise");

    let agingSheetData: any[] = this.agingData.map((a, i) => ({
      'S.No': i + 1 + ((this.pageNo - 1) * this.pageSize),
      'Complaint No': a.complaintNo || a.complaint_no,
      'Complainant Type': a.complainantType || a.complainant_type,
      'Complaint Type': a.complaintType || a.complaint_type,
      'Assign To': a.assignTo || a.assign_to,
      'Days Elapsed': ((a.days || a.daysElapsed || a.days_elapsed) || 0) + ' Days',
      'District': a.district,
      'Status': a.status
    }));

    if (agingSheetData.length === 0) {
      agingSheetData = [{
        'S.No': 'No Data',
        'Complaint No': '',
        'Complainant Type': '',
        'Complaint Type': '',
        'Assign To': '',
        'Days Elapsed': '',
        'District': '',
        'Status': ''
      }];
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(agingSheetData), "details");

    const dateStr = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    XLSX.writeFile(wb, `Delay_Complaints_Report_${dateStr}.xlsx`);
  }
}
